// The app runtime: every long-lived object of the web app, created once at startup (main.tsx) or
// per test. Providers read it from context; nothing here renders.
import { configureApiClient } from '@batiflow/api-client';
import {
  ApiError,
  createApiFetcher,
  createSessionStore,
  loginUrl,
  pendingActionRequest,
  savePendingAction,
  selectCsrfToken,
  stepUpUrl,
  systemClock,
  takePendingAction,
  createIdempotencyKeyFactory,
  type ApiFetcher,
  type ApiRequest,
  type FetchLike,
  type SessionStore,
  type SocketFactory,
  type StoragePort,
} from '@batiflow/client-core';
import { LogoutResponseSchema } from '@batiflow/contracts';
import { createI18nInstance } from '@batiflow/i18n';
import { QueryClient } from '@tanstack/react-query';
import type { i18n as I18nInstance, Resource } from 'i18next';
import { createEventBus, type EventBus } from '@/shared/event-bus';
import { createErrorReporter, type ErrorReporter } from '@/shared/error-reporting';
import { createBrowserStorage } from '@/shared/storage';
import { createRealtimeStore, type RealtimeStore } from '@/features/realtime/store';
import { localeBackend, WEB_NAMESPACES } from './i18n-backend';
import { createLocaleStore, type LocaleStore } from './locale-store';

/** Full-page navigation, injected so tests can observe redirects (jsdom cannot navigate). */
export interface AppNavigation {
  assign: (url: string) => void;
  /** Current path with its query string, e.g. `/o/abc/activity?tab=1`. */
  currentPath: () => string;
}

/** What happened to the action that was waiting for a step-up (12-frontend.md §4.5). */
export type PendingActionOutcome = 'none' | 'replayed' | 'failed' | 'discarded';

export interface AppRuntime {
  storage: StoragePort;
  /** This tab only (sessionStorage): the action waiting for a step-up. */
  sessionStorage: StoragePort;
  localeStore: LocaleStore;
  i18n: I18nInstance;
  /** Resolves once the translations of the language in use are loaded (render after it). */
  i18nReady: Promise<void>;
  eventBus: EventBus;
  fetcher: ApiFetcher;
  sessionStore: SessionStore;
  queryClient: QueryClient;
  /** Connection state, presence of others and the person's own status (Zustand, not the query cache). */
  realtimeStore: RealtimeStore;
  /** Realtime switch and, for tests, the socket factory (a fake socket). */
  realtime: { enabled: boolean; socketFactory?: SocketFactory };
  navigation: AppNavigation;
  errorReporter: ErrorReporter;
  /** Clears the session and goes to the sign-in page (once, however many calls report a 401). */
  redirectToLogin: () => void;
  /**
   * Ends the server session (BFF logout, AUTH-BE-01), then goes to Keycloak's end-session page,
   * which comes back to /signed-out. Resolves false when the server did not confirm: the caller
   * must tell the user, the session may still be alive.
   */
  signOut: () => Promise<boolean>;
  /**
   * Remembers `request` (refused with STEP_UP_REQUIRED) and goes to the step-up; the action is
   * replayed once when the user comes back. Returns false when the request cannot be replayed
   * (the user then simply repeats the action after the step-up).
   */
  startStepUp: (request: ApiRequest) => boolean;
  /** Replays the action saved by startStepUp, once; discards it when the step-up failed. */
  replayPendingAction: (stepUpFailed: boolean) => Promise<PendingActionOutcome>;
}

export interface RuntimeOptions {
  fetch?: FetchLike;
  storage?: StoragePort;
  sessionStorage?: StoragePort;
  navigation?: AppNavigation;
  release?: string;
  /** `false` keeps the realtime connection closed; `{ socketFactory }` injects a fake socket. */
  realtime?: false | { socketFactory?: SocketFactory };
  /**
   * Every translation up front (tests: `resources` of @batiflow/i18n). Without it the app loads
   * the language in use on demand (src/app/i18n-backend.ts).
   */
  i18nResources?: Resource;
}

const browserNavigation: AppNavigation = {
  assign: (url) => {
    window.location.assign(url);
  },
  currentPath: () => window.location.pathname + window.location.search,
};

const browserFetch: FetchLike = (url, init) => window.fetch(url, init as RequestInit);

/** TanStack Query defaults (docs/plan/12-frontend.md section 3). */
export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        // Retry twice, but never on a client error: repeating a 4xx cannot make it succeed.
        retry: (failureCount, error) =>
          failureCount < 2 &&
          !(error instanceof ApiError && error.status >= 400 && error.status < 500),
      },
    },
  });
}

export function createAppRuntime(options: RuntimeOptions = {}): AppRuntime {
  const storage = options.storage ?? createBrowserStorage();
  const sessionStorage = options.sessionStorage ?? createBrowserStorage('session');
  const navigation = options.navigation ?? browserNavigation;
  const fetchImpl = options.fetch ?? browserFetch;

  const localeStore = createLocaleStore(storage);
  const { i18n, ready: i18nReady } = createI18nInstance({
    locale: localeStore.getLocale(),
    namespaces: WEB_NAMESPACES,
    ...(options.i18nResources ? { resources: options.i18nResources } : { backend: localeBackend }),
    ...(import.meta.env.DEV
      ? {
          onMissingKey: (_lng: readonly string[], ns: string, key: string) => {
            console.warn(`[i18n] missing key ${ns}:${key}`);
          },
        }
      : {}),
  });
  // A new language is loaded first, then the app switches to it.
  localeStore.subscribe(() => {
    void i18n.changeLanguage(localeStore.getLocale());
  });

  const eventBus = createEventBus();
  const queryClient = createAppQueryClient();
  const realtimeStore = createRealtimeStore();
  const holder: { session?: SessionStore } = {};
  // Set once the page is navigating away (sign-in or sign-out): later 401s and the anonymous
  // session must not start a second navigation that would override the first one.
  let leaving = false;

  const redirectToLogin = () => {
    if (leaving) return;
    leaving = true;
    holder.session?.getState().clear();
    queryClient.clear();
    navigation.assign(loginUrl(navigation.currentPath(), { locale: localeStore.getLocale() }));
  };

  const fetcher = createApiFetcher({
    fetch: fetchImpl,
    getLocale: () => localeStore.getLocale(),
    getCsrfToken: () => (holder.session ? selectCsrfToken(holder.session.getState()) : null),
    randomId: () => crypto.randomUUID(),
    handlers: {
      onUnauthorized: redirectToLogin,
      onMfaRequired: () => {
        holder.session?.getState().setMfaRequired(true);
      },
      onStepUpRequired: (error, request) => {
        eventBus.emit('stepUpRequired', { error, request });
      },
      onPlanLimitReached: (error) => {
        eventBus.emit('planLimitReached', error);
      },
    },
  });
  configureApiClient(fetcher);

  const sessionStore = createSessionStore({
    storage,
    loadMe: async () => {
      try {
        // No global handlers: a 401 here means "anonymous"; protected routes redirect through
        // AuthGate, public pages (/signed-out) must not.
        const response = await fetcher<unknown>({
          method: 'GET',
          url: '/api/v1/me',
          skipHandlers: true,
        });
        return response.data;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
  });
  holder.session = sessionStore;

  // The user's own locale wins as soon as /me is loaded.
  let lastMeLocale: string | undefined;
  sessionStore.subscribe((state) => {
    const locale = state.me?.user.locale;
    if (locale && locale !== lastMeLocale) {
      lastMeLocale = locale;
      localeStore.setLocale(locale);
    }
  });

  const errorReporter = createErrorReporter({
    fetcher,
    release: options.release ?? __BP_RELEASE__,
    getPath: () => navigation.currentPath(),
  });

  const signOut = async (): Promise<boolean> => {
    let endSessionUrl: string;
    // Leaving from now on: once the server session is gone, a background request answered 401
    // must not start a sign-in that races the end of the Keycloak session (it then fails).
    leaving = true;
    try {
      const response = await fetcher<unknown>({ method: 'POST', url: '/api/v1/auth/logout' });
      endSessionUrl = LogoutResponseSchema.parse(response.data).endSessionUrl;
    } catch (error) {
      leaving = false;
      // The session had already ended: the person is signed out, as the 401 handler would say.
      if (error instanceof ApiError && error.status === 401) {
        redirectToLogin();
        return true;
      }
      return false;
    }
    sessionStore.getState().clear();
    queryClient.clear();
    // Keycloak ends its own session, then comes back to /signed-out (post_logout_redirect_uri).
    navigation.assign(endSessionUrl);
    return true;
  };

  const newIdempotencyKey = createIdempotencyKeyFactory(systemClock, (length) =>
    crypto.getRandomValues(new Uint8Array(length)),
  );

  const startStepUp = (request: ApiRequest): boolean => {
    // Never kept when the body holds secrets (key material included, client-core holdsSecrets): the
    // user then repeats the action after the identity check (EINV-FE-01: picks the files again).
    const saved = savePendingAction(sessionStorage, systemClock, request, newIdempotencyKey);
    navigation.assign(stepUpUrl(navigation.currentPath()));
    return saved;
  };

  const replayPendingAction = async (stepUpFailed: boolean): Promise<PendingActionOutcome> => {
    const action = takePendingAction(sessionStorage, systemClock);
    if (!action) return 'none';
    if (stepUpFailed) return 'discarded';
    try {
      await fetcher(pendingActionRequest(action));
      await queryClient.invalidateQueries();
      return 'replayed';
    } catch {
      return 'failed';
    }
  };

  return {
    storage,
    sessionStorage,
    localeStore,
    i18n,
    i18nReady,
    eventBus,
    fetcher,
    sessionStore,
    queryClient,
    realtimeStore,
    realtime:
      options.realtime === false
        ? { enabled: false }
        : {
            enabled: true,
            ...(options.realtime?.socketFactory
              ? { socketFactory: options.realtime.socketFactory }
              : {}),
          },
    navigation,
    errorReporter,
    redirectToLogin,
    signOut,
    startStepUp,
    replayPendingAction,
  };
}
